import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Switch,
  Select,
  Space,
  Card,
  message,
  Divider,
  Alert,
  Spin,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../FirebaseConfig";
import dayjs from "dayjs";

/* DND */
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const { Option } = Select;

/* ---------- FIELD TYPES ---------- */
const FIELD_TYPES = [
  { label: "Switch", value: "switch" },
  { label: "Checkbox", value: "checkbox" },
  { label: "Radio Button", value: "radio" },
  { label: "Input Field", value: "input" },
  { label: "Textarea", value: "textarea" },
  { label: "Dropdown", value: "dropdown" },
  { label: "Multi Select Dropdown", value: "multiselect" },
  { label: "Number", value: "number" },
];

/* ---------- UTILS ---------- */
const arrayMove = (arr, from, to) => {
  const copy = [...arr];
  const item = copy.splice(from, 1)[0];
  copy.splice(to, 0, item);
  return copy;
};

/* ---------- SORTABLE ---------- */
const SortableItem = ({ id, children }) => {
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
    >
      {children(listeners)}
    </div>
  );
};

const AdminAppPage = () => {
  const { workspaceId, appId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [alertConfig, setAlertConfig] = useState(null);

  const appDocRef = doc(db, "dive_hrzn_apps", workspaceId, "apps", appId);
  const workspaceDocRef = doc(
    db,
    "dive_hrzn_apps",
    workspaceId,
    "workspace",
    "accountDetails",
  );

  /* ---------- LOAD ---------- */
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const wsSnap = await getDoc(workspaceDocRef);
        if (!wsSnap.exists()) {
          setBlocked(true);
          setAlertConfig({ type: "error", message: "Workspace not found" });
          return;
        }

        const ws = wsSnap.data();
        if (!ws.activeStatus) {
          setBlocked(true);
          setAlertConfig({ type: "error", message: "Workspace inactive" });
          return;
        }

        const daysLeft = dayjs(ws.planExpireDate.toDate()).diff(dayjs(), "day");
        if (daysLeft < 0) {
          setBlocked(true);
          setAlertConfig({ type: "error", message: "Subscription expired" });
          return;
        }

        const appSnap = await getDoc(appDocRef);
        form.setFieldsValue(
          appSnap.exists() ? appSnap.data() : { enabled: true, sections: [] },
        );
      } catch (e) {
        console.error(e);
        message.error("Failed to load");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [workspaceId, appId]);

  if (loading)
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );

  if (blocked)
    return (
      <div style={{ padding: 16 }}>
        <Alert {...alertConfig} showIcon />
      </div>
    );

  /* ---------- SAVE ---------- */
  const onFinish = async (values) => {
    try {
      setSaving(true);
      await setDoc(appDocRef, { ...values, updatedAt: new Date() });
      message.success("Saved successfully");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={`Admin – ${workspaceId} / ${appId}`}
      style={{ maxWidth: 900, margin: "auto" }}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item name="enabled" label="Enable App" valuePropName="checked">
          <Switch />
        </Form.Item>

        {/* ================= SECTIONS ================= */}
        <Form.List name="sections">
          {(sections, sectionOps) => (
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={({ active, over }) => {
                if (!over || active.id === over.id) return;
                const current = form.getFieldValue("sections") || [];
                form.setFieldsValue({
                  sections: arrayMove(current, active.id, over.id),
                });
              }}
            >
              <SortableContext
                items={sections.map((_, i) => i)}
                strategy={verticalListSortingStrategy}
              >
                {sections.map(({ key, name }) => (
                  <SortableItem key={key} id={name}>
                    {(listeners) => (
                      <Card
                        size="small"
                        style={{ marginBottom: 16 }}
                        title={
                          <Space>
                            <HolderOutlined {...listeners} />
                            <Form.Item
                              name={[name, "title"]}
                              rules={[{ required: true }]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input placeholder="Section title" />
                            </Form.Item>
                          </Space>
                        }
                        extra={
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => sectionOps.remove(name)}
                          />
                        }
                      >
                        {/* ================= FIELDS ================= */}
                        <Form.List name={[name, "fields"]}>
                          {(fields, fieldOps) => (
                            <DndContext
                              collisionDetection={closestCenter}
                              onDragEnd={({ active, over }) => {
                                if (!over || active.id === over.id) return;

                                const allSections =
                                  form.getFieldValue("sections") || [];

                                const updatedSections = allSections.map(
                                  (sec, idx) => {
                                    if (idx !== name) return sec;
                                    return {
                                      ...sec,
                                      fields: arrayMove(
                                        sec.fields || [],
                                        active.id,
                                        over.id,
                                      ),
                                    };
                                  },
                                );

                                form.setFieldsValue({
                                  sections: updatedSections,
                                });
                              }}
                            >
                              <SortableContext
                                items={fields.map((_, i) => i)}
                                strategy={verticalListSortingStrategy}
                              >
                                {fields.map(({ key, name: f }) => (
                                  <SortableItem key={key} id={f}>
                                    {(listeners) => (
                                      <Card
                                        size="small"
                                        style={{ marginBottom: 8 }}
                                      >
                                        <Space wrap>
                                          <HolderOutlined {...listeners} />

                                          <Form.Item
                                            name={[f, "label"]}
                                            rules={[{ required: true }]}
                                          >
                                            <Input
                                              size="small"
                                              placeholder="Label"
                                            />
                                          </Form.Item>

                                          <Form.Item name={[f, "type"]}>
                                            <Select size="small">
                                              {FIELD_TYPES.map((t) => (
                                                <Option
                                                  key={t.value}
                                                  value={t.value}
                                                >
                                                  {t.label}
                                                </Option>
                                              ))}
                                            </Select>
                                          </Form.Item>

                                          <Form.Item
                                            name={[f, "enabled"]}
                                            valuePropName="checked"
                                          >
                                            <Switch size="small" />
                                          </Form.Item>

                                          <Button
                                            danger
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={() => fieldOps.remove(f)}
                                          />
                                        </Space>
                                      </Card>
                                    )}
                                  </SortableItem>
                                ))}
                              </SortableContext>

                              <Button
                                type="dashed"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() =>
                                  fieldOps.add({
                                    label: "",
                                    type: "input",
                                    enabled: true,
                                  })
                                }
                                block
                              >
                                Add Field
                              </Button>
                            </DndContext>
                          )}
                        </Form.List>
                      </Card>
                    )}
                  </SortableItem>
                ))}
              </SortableContext>

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => sectionOps.add({ title: "", fields: [] })}
                block
              >
                Add Section
              </Button>
            </DndContext>
          )}
        </Form.List>

        <Divider />
        <Button type="primary" htmlType="submit" loading={saving} block>
          Save
        </Button>
      </Form>
    </Card>
  );
};

export default AdminAppPage;
